import { Timestamp } from "firebase/firestore";

/**
 * FilersHub Auth and Role Types
 */
export type FilersHubStaffRole = "FirmOwner" | "Manager" | "Tax Pro" | "Unassigned";

export enum UserRole {
  CLIENT = "CLIENT",
  FirmOwner = "FirmOwner",
  Manager = "Manager",
  TaxPro = "Tax Pro",
}

// Update: UserRoleString to support flexible role naming conventions from various integrations
export type UserRoleString = UserRole.CLIENT | FilersHubStaffRole | "FIRM_OWNER" | "MANAGER" | "TAX_PRO" | string;

// Fix: Added exported isStaffRole helper to resolve "no exported member" error in AuthContext.tsx
export const isStaffRole = (role?: string | null): boolean => {
  if (!role) return false;

  // Canonicalize: "Tax Pro", "taxpro", "tax_pro" all become "taxpro"
  const r = role
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  return (
    r === "firmowner" ||
    r === "owner" ||
    r === "admin" ||
    r === "manager" ||
    r === "taxpro" ||
    r === "staff" ||
    r === "preparer"
  );
};

/**
 * Firm Configuration Types
 */
export interface CRMConfig {
  connected: boolean;
  locationId?: string;
  connectedAt?: Timestamp;
  authorizedBy?: string;
}

export interface WebhookConfig {
  outboundUrl?: string;
  secretKey?: string;
  events: {
    docUploaded: boolean;
    docSigned: boolean;
    statusChanged: boolean;
    messageReceived: boolean;
  };
}

export interface FirmSettingsDoc {
  name: string;
  logoUrl?: string;
  brandColor?: string;
  updatedAt?: Timestamp;
  webhookConfig?: WebhookConfig;
  crmConfig?: CRMConfig;
}

export interface FirmUpdate {
  id: string;
  title: string;
  content: string;
  createdAt: any;
  authorName: string;
}

/**
 * User and Profile Types
 */
export interface UserDoc {
  uid: string;
  email: string;
  displayName: string;
  role: UserRoleString;
  firmId: string;
  phone?: string;
  createdAt: Timestamp;
  avatarUrl?: string;
  messageSignature?: string;
  locationId?: string;
}

export interface AppUserDoc {
  uid: string;
  email: string;
  firmId: string;
  role: FilersHubStaffRole;
  linkedUserId?: string;
  updatedAt?: any;
  displayName?: string;
}

// Unified SyncedStaffUser properties to support both CRM and GHL naming patterns
export type SyncedStaffUser = {
  crmUserId: string;
  ghlUserId: string;
  email: string | null;
  name: string | null;
  crmRole: string | null;
  ghlRole: string | null;
  filershubRole: FilersHubStaffRole;
  active?: boolean;
  updatedAt?: any;
};

/**
 * Tax Case and Document Types
 */

export enum TaxStatus {
  INFO_RECEIVED = "Info Received",
  IN_PROGRESS = "In Progress",
  IN_REVIEW = "In Review",
  READY_TO_SIGN = "Ready to Sign",
  FILED = "Filed",
  ACCEPTED = "Accepted",
  NEEDS_INFO = "Needs Info",
  PAYMENT_NEEDED = "Payment Needed",
  REJECTED = "Rejected",
}

export interface TaxCaseDoc {
  id: string;
  firmId: string;
  clientId: string;
  clientName: string;
  year: number;
  status: TaxStatus | string;
  assignedStaffId?: string;
  assignedStaffName?: string;
  lastUpdated: Timestamp;
  filingType: string;
  agi?: number;
  federalRefund?: number;
  stateRefund?: number;
}

export interface SignatureField {
  id: string;
  x: number;
  y: number;
  signed: boolean;
  signedAt?: Timestamp;
  ipAddress?: string;
}

export interface TaxCaseDocumentDoc {
  id: string;
  firmId: string;
  caseId: string;
  fileName: string;
  storagePath: string;
  mimeType: string;
  sizeBytes: number;
  category: string;
  uploadedBy: string;
  uploadedAt: Timestamp;
  status: "PENDING" | "APPROVED" | "REJECTED" | "ADDED_TO_RETURN" | "ISSUE";
  signatureFields?: SignatureField[];
  clientId: string;
  clientName: string;
  year: number;
  filingType: string;
}

export interface TaxMessage {
  id: string;
  senderId: string;
  content: string;
  timestamp: Timestamp;
  readBy: string[];
}

export interface TaxTask {
  id: string;
  title: string;
  description: string;
  status: "PENDING" | "COMPLETED";
  type: "UPLOAD" | "SIGN" | "GENERAL";
  createdAt: Timestamp;
}

export interface UserProfile extends Omit<UserDoc, "createdAt" | "role"> {
  role: UserRole;
  createdAt: string;
}

export interface TaxCase extends Omit<TaxCaseDoc, "lastUpdated" | "status"> {
  status: TaxStatus;
  lastUpdated: any;
}

export interface TaxDocument extends Omit<TaxCaseDocumentDoc, "uploadedAt"> {
  uploadedAt: string;
}
